import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { z } from 'zod'
import {
	Form, FormControl, FormMessage, FormField, FormItem, FormLabel,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { EditableSectionHeader } from './editable-section-header'
import { SubjectServicesTable } from './subject-services-table'
import {
	OBJECT_PURPOSES,
	type ObjectPurpose, type SubjectServiceRow, type SubjectValues,
} from './types'

// The cadastral number identifies the premises the CONTRACT lets (п. 3 ст. 607
// ГК РФ — without it the lease is not concluded). Only the lease-contract
// templates print it; an act, appendix or registration application must not be
// blocked by a field its template never mentions.
const makeSubjectSchema = (requireCadastral: boolean) => z.object({
	propertyId:      z.string(),
	objectType:      z.string(),
	address:         z.string(),
	cadastralNumber: requireCadastral
		? z.string().trim().min(1, 'deals.documents.wizard.subject.validation.cadastralRequired')
		: z.string(),
	area:            z.string().refine((v) => v === '' || !Number.isNaN(parseFloat(v.replace(',', '.'))), {
		message: 'invalid-number',
	}),
	purpose:         z.enum(OBJECT_PURPOSES as [ObjectPurpose, ...ObjectPurpose[]]),
})

type SubjectFormSchema = z.infer<ReturnType<typeof makeSubjectSchema>>

interface StepSubjectProps {
	initial: SubjectValues
	formId:  string
	/** Drives which fields are hard-required — see makeSubjectSchema. */
	documentType: string
	onValid: (values: SubjectValues) => void
}

export function StepSubject({ initial, formId, documentType, onValid }: StepSubjectProps) {
	const { t } = useTranslation()

	// Services live in local state (rows + numbers, no form-level validation yet).
	const [services, setServices] = useState<SubjectServiceRow[]>(initial.services)

	// Pencil-toggles: each section is read-only (gray inputs auto-filled from
	// Property) until the user clicks ✏️. Toggling is local to this wizard
	// instance — it doesn't write back to the source.
	const [objectEditable,   setObjectEditable]   = useState(false)
	const [servicesEditable, setServicesEditable] = useState(false)

	const form = useForm<SubjectFormSchema>({
		resolver: zodResolverI18n(makeSubjectSchema(documentType === 'LEASE_CONTRACT')),
		defaultValues: {
			propertyId:      initial.propertyId,
			objectType:      initial.objectType,
			address:         initial.address,
			cadastralNumber: initial.cadastralNumber,
			area:            initial.area,
			purpose:         initial.purpose,
		},
		mode: 'onChange',
	})

	function handleSubmit(values: SubjectFormSchema) {
		onValid({
			...initial,
			...values,
			purpose: values.purpose as ObjectPurpose,
			services,
		})
	}

	return (
		<Form {...form}>
			<form
				id={formId}
				onSubmit={form.handleSubmit(handleSubmit)}
				className="flex flex-col gap-5"
			>
				<section className="flex flex-col gap-3">
					<EditableSectionHeader
						title={t('deals.documents.wizard.subject.objectTitle')}
						editable={objectEditable}
						onToggle={() => setObjectEditable((v) => !v)}
						editLabel={t('deals.documents.wizard.subject.editObject')}
					/>

					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="objectType"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.subject.objectType')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											disabled={!objectEditable}
											placeholder={t('deals.documents.wizard.subject.objectTypePlaceholder')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="address"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.subject.address')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											disabled={!objectEditable}
											placeholder={t('deals.documents.wizard.subject.addressPlaceholder')}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>

					<div className="flex gap-2">
						<FormField
							control={form.control}
							name="cadastralNumber"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.subject.cadastralNumber')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={!objectEditable} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="area"
							render={({ field }) => (
								<FormItem className="flex-1">
									<FormLabel>{t('deals.documents.wizard.subject.area')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											disabled={!objectEditable}
											inputMode="decimal"
											suffix={t('deals.documents.wizard.subject.areaUnit')}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="purpose"
						render={({ field }) => (
							<FormItem className="w-1/2 pr-1">
								<FormLabel>{t('deals.documents.wizard.subject.purpose')}</FormLabel>
								<Select value={field.value} onValueChange={field.onChange}>
									<FormControl>
										<SelectTrigger size="md" className="w-full">
											<SelectValue />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="residence">
											{t('deals.documents.wizard.subject.purposeResidence')}
										</SelectItem>
										<SelectItem value="commercial">
											{t('deals.documents.wizard.subject.purposeCommercial')}
										</SelectItem>
										<SelectItem value="mixed">
											{t('deals.documents.wizard.subject.purposeMixed')}
										</SelectItem>
									</SelectContent>
								</Select>
							</FormItem>
						)}
					/>
				</section>

				<section className="flex flex-col gap-3">
					<EditableSectionHeader
						title={t('deals.documents.wizard.subject.additionalTitle')}
						editable={servicesEditable}
						onToggle={() => setServicesEditable((v) => !v)}
						editLabel={t('deals.documents.wizard.subject.editServices')}
					/>

					<SubjectServicesTable
						services={services}
						editable={servicesEditable}
						onChange={setServices}
					/>
				</section>
			</form>
		</Form>
	)
}
